import React, { useEffect, useState } from 'react';
import { CategorySelector } from './CategorySelector';
import { config } from '../config';
import { fetchWithRetry } from '../utils/fetchWithRetry';

interface Receipt {
    currency: string;
}

interface Item {
    item_id: string;
    raw_name: string;
    qty: number | null;
    unit_price: number | null;
    total_gross: number | null;
    vat_rate: number | null;
    cat_ai: string | null;
    category_id: string | null;
    category_path: string | null;
    category_source: string | null;
    parent_item_id: string | null;
    discounted: boolean;
    adjusted_total_gross: number | null;
    pack_qty: number | null;
    pack_unit: string | null;
    std_qty: number | null;
    std_unit: string | null;
    price_per_100: number | null;
    price_adj_per_100: number | null;
}

interface ItemDetailModalProps {
    item: Item;
    receipt: Receipt;
    allItems: Item[];
    categoryColors: { [category: string]: string };
    onClose: () => void;
    getAuthToken: () => Promise<string | null>;
    onRefresh?: () => void;
}

const ItemDetailModal: React.FC<ItemDetailModalProps> = ({
    item,
    receipt,
    allItems,
    categoryColors,
    onClose,
    getAuthToken,
    onRefresh,
}) => {
    const [showCategorySelector, setShowCategorySelector] = useState(false);
    const [updateSuccess, setUpdateSuccess] = useState<{ itemsUpdated: number; categoryPath: string } | null>(null);

    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                if (showCategorySelector) {
                    setShowCategorySelector(false);
                } else {
                    onClose();
                }
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose, showCategorySelector]);

    // Get currency symbol
    const getCurrencySymbol = (currency: string) => {
        const symbols: { [key: string]: string } = {
            'EUR': '€',
            'USD': '$',
            'GBP': '£',
            'CZK': 'Kč',
            'PLN': 'zł'
        };
        return symbols[currency] || currency;
    };

    // Format volume with smart unit conversion
    const formatVolume = (qty: number, unit: string): string => {
        if (unit === 'g' && qty >= 1000) {
            const kg = qty / 1000;
            return `${kg.toFixed(2).replace('.', ',')} kg`;
        }
        if (unit === 'ml' && qty >= 1000) {
            const l = qty / 1000;
            return `${l.toFixed(2).replace('.', ',')} l`;
        }
        return `${qty} ${unit}`;
    };

    // Handle category update
    const handleCategoryUpdate = async (categoryId: string, categoryPath: string) => {
        try {
            const token = await getAuthToken();
            if (!token) {
                throw new Error('Nie ste prihlásený');
            }

            const response = await fetchWithRetry(`${config.apiUrl}/updateItemCategory`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Auth-Token': token
                },
                credentials: 'include',
                body: JSON.stringify({
                    item_id: item.item_id,
                    category_id: categoryId
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Nepodarilo sa aktualizovať kategóriu');
            }

            const result = await response.json();

            // Show success message
            setUpdateSuccess({
                itemsUpdated: result.itemsUpdated,
                categoryPath: result.category.path
            });

            // Hide category selector
            setShowCategorySelector(false);

            // Trigger parent refresh to get updated data
            if (onRefresh) {
                onRefresh();
            }

            // Auto-hide success message after 5 seconds
            setTimeout(() => {
                setUpdateSuccess(null);
            }, 5000);

        } catch (error: any) {
            throw new Error(error.message || 'Nepodarilo sa aktualizovať kategóriu');
        }
    };

    // Find child discount items
    const childDiscounts = allItems.filter(
        (i) => i.parent_item_id === item.item_id && (i.total_gross ?? 0) < 0
    );

    const hasDiscount = item.discounted && childDiscounts.length > 0;

    // Prefer category_path over old cat_ai
    const category = item.category_path || item.cat_ai || 'Nezaradené';

    // Extract the deepest level for color matching (used in pie chart)
    // Format: "Level1 > Level2 > Level3" -> we want the last part
    const categoryForColor = category.includes(' > ')
        ? category.split(' > ').pop()?.trim() || category
        : category;
    const categoryColor = categoryColors[categoryForColor] || '#E7E9ED';

    // Check if window width is small (for mobile full-screen)
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: isMobile ? 'stretch' : 'center',
                justifyContent: 'center',
                zIndex: 2000,
                padding: isMobile ? '0' : '20px',
            } as React.CSSProperties}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    backgroundColor: 'white',
                    borderRadius: isMobile ? '0' : '12px',
                    padding: '30px',
                    maxWidth: isMobile ? '100%' : '500px',
                    width: '100%',
                    maxHeight: isMobile ? '100%' : '90vh',
                    overflowY: 'auto',
                    position: 'relative',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                }}
            >
                {/* Close button */}
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '15px',
                        right: '15px',
                        background: 'none',
                        border: 'none',
                        fontSize: '24px',
                        cursor: 'pointer',
                        color: '#999',
                        lineHeight: 1,
                        padding: '5px',
                    }}
                    aria-label="Zavrieť"
                >
                    ×
                </button>

                {/* Header - Item Name */}
                <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#333', paddingRight: '30px' }}>
                    {item.raw_name}
                </h3>

                {/* Price Section - Hero */}
                <div style={{ marginBottom: '25px' }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#2c3e50' }}>
                        {item.qty ?? 0} × {item.unit_price ? item.unit_price.toFixed(2) : '0.00'} {getCurrencySymbol(receipt.currency)} = {' '}
                        {!hasDiscount ? (
                            <span>{item.total_gross ? item.total_gross.toFixed(2) : '0.00'} {getCurrencySymbol(receipt.currency)}</span>
                        ) : (
                            <span style={{ color: '#999', textDecoration: 'line-through', fontSize: '16px' }}>
                                {item.total_gross ? item.total_gross.toFixed(2) : '0.00'} {getCurrencySymbol(receipt.currency)}
                            </span>
                        )}
                    </div>
                </div>

                {/* Volume Information (if available) */}
                {item.std_qty && item.std_unit && (
                    <div style={{ marginBottom: '25px', backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '8px' }}>
                        <div style={{ marginBottom: '10px', fontSize: '14px', color: '#555' }}>
                            📊 <strong>Zakúpený objem:</strong> {formatVolume(item.std_qty, item.std_unit)}
                        </div>
                        {item.price_per_100 && (
                            <div style={{ fontSize: '14px', color: '#555' }}>
                                💰 <strong>Cena za 100{item.std_unit}:</strong> {item.price_per_100.toFixed(2)} {getCurrencySymbol(receipt.currency)}
                            </div>
                        )}
                    </div>
                )}

                {/* Category & VAT */}
                <div style={{ marginBottom: '25px' }}>
                    <div style={{ marginBottom: '10px' }}>
                        <div style={{ marginBottom: '8px', fontSize: '14px', color: '#555' }}>
                            🏷️ <strong>Kategória:</strong>
                        </div>
                        <div
                            style={{
                                backgroundColor: categoryColor,
                                color: '#333',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: '500',
                                display: 'inline-block'
                            }}
                        >
                            {category}
                        </div>
                    </div>
                    <div style={{ fontSize: '14px', color: '#555' }}>
                        📝 <strong>Sadzba DPH:</strong> {item.vat_rate ? (item.vat_rate % 1 === 0 ? item.vat_rate.toFixed(0) : item.vat_rate.toFixed(2)) : '0'}%
                    </div>
                </div>

                {/* Discount Section (if applicable) */}
                {hasDiscount && (
                    <div style={{ marginBottom: '25px', borderTop: '2px solid #e0e0e0', paddingTop: '20px' }}>
                        <div style={{ textAlign: 'center', fontSize: '14px', fontWeight: 'bold', color: '#999', marginBottom: '15px' }}>
                            ZĽAVA
                        </div>

                        {/* List all child discounts */}
                        {childDiscounts.map((discount) => (
                            <div
                                key={discount.item_id}
                                style={{
                                    marginBottom: '10px',
                                    fontSize: '14px',
                                    color: '#555',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <span>🎫 {discount.raw_name}</span>
                                <span style={{ fontWeight: 'bold', color: '#e74c3c' }}>
                                    {discount.total_gross ? discount.total_gross.toFixed(2) : '0.00'} {getCurrencySymbol(receipt.currency)}
                                </span>
                            </div>
                        ))}

                        <div style={{ borderTop: '1px solid #f0f0f0', marginTop: '15px', paddingTop: '15px' }}>
                            <div style={{ marginBottom: '10px', fontSize: '16px', fontWeight: 'bold' }}>
                                <span>Celková cena po zľave:</span>{' '}
                                <span style={{ color: '#e74c3c' }}>
                                    {item.adjusted_total_gross ? item.adjusted_total_gross.toFixed(2) : '0.00'} {getCurrencySymbol(receipt.currency)}
                                </span>
                            </div>
                            {item.price_adj_per_100 && item.std_unit && (
                                <div style={{ fontSize: '14px', color: '#555' }}>
                                    Po zľave upravená cena za 100{item.std_unit}: {item.price_adj_per_100.toFixed(2)} {getCurrencySymbol(receipt.currency)}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Success Message */}
                {updateSuccess && (
                    <div style={{
                        marginBottom: '20px',
                        padding: '15px',
                        backgroundColor: '#4caf50',
                        color: 'white',
                        borderRadius: '8px',
                        fontSize: '14px',
                        textAlign: 'center'
                    }}>
                        ✓ Kategória úspešne zmenená! Aktualizovaných položiek: {updateSuccess.itemsUpdated}
                    </div>
                )}

                {/* Category Selector or Edit Link */}
                {showCategorySelector ? (
                    <CategorySelector
                        currentCategoryId={item.category_id || undefined}
                        itemId={item.item_id}
                        onSave={handleCategoryUpdate}
                        onCancel={() => setShowCategorySelector(false)}
                        getAuthToken={getAuthToken}
                    />
                ) : (
                    <div style={{ marginTop: '30px', textAlign: 'center' }}>
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                setShowCategorySelector(true);
                            }}
                            style={{
                                fontSize: '14px',
                                color: '#2196F3',
                                textDecoration: 'none',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            ✏️ Upraviť kategóriu
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ItemDetailModal;
